/**
 * /marketplace/new — Post a new marketplace listing (Phase 1).
 *
 * Signed-in only. Form captures title, description, category, listing_type
 * (for_sale | looking_for), condition, price OR accepts_offers, photos
 * (upload + URL), and radius level. Location is taken from LocalityContext.
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useLocality } from "@/contexts/LocalityContext";
import { Camera, Loader2, Plus, X, DollarSign, Send, Link as LinkIcon } from "lucide-react";

export default function MarketplaceNew() {
  const { user } = useAuth();
  const { locality } = useLocality();
  const nav = useNavigate();
  const [meta, setMeta] = useState(null);
  const [busy, setBusy] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [photoUrl, setPhotoUrl] = useState("");
  const [photoUrls, setPhotoUrls] = useState([]);
  const [form, setForm] = useState({
    title: "", description: "", category: "other", listing_type: "for_sale",
    condition: "good", price: "", accepts_offers: false, radius_level: "city",
  });
  const fileRef = useRef(null);

  useEffect(() => {
    http.get("/marketplace/meta").then((r) => setMeta(r.data)).catch(() => {});
  }, []);

  if (!user) {
    return (
      <div className="max-w-md mx-auto px-6 py-16 text-center">
        <p className="text-sm">You need to be signed in to post a listing.</p>
        <a href="/login" className="wofy-btn-primary mt-4 inline-flex">Sign in</a>
      </div>
    );
  }

  if (!locality?.lat) {
    return (
      <div className="max-w-md mx-auto px-6 py-16 text-center">
        <p className="text-sm">
          Please <b>set your location</b> before posting so buyers nearby can find your listing.
        </p>
      </div>
    );
  }

  const onFile = (e) => {
    const files = [...(e.target.files || [])];
    Promise.all(files.map((f) => new Promise((res) => {
      const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(f);
    }))).then((urls) => setPhotos((p) => [...p, ...urls].slice(0, 8)));
  };

  const addPhotoUrl = () => {
    if (!photoUrl.trim().startsWith("http")) return;
    setPhotoUrls((u) => [...u, photoUrl.trim()].slice(0, 8));
    setPhotoUrl("");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (photos.length + photoUrls.length > 8) { toast.error("Maximum 8 photos."); return; }
    if (!form.title.trim()) { toast.error("Title is required."); return; }
    setBusy(true);
    try {
      const { data } = await http.post("/marketplace", {
        title: form.title.trim(),
        description: form.description.trim() || null,
        category: form.category,
        listing_type: form.listing_type,
        condition: form.condition,
        price: form.price === "" ? null : Number(form.price),
        accepts_offers: form.accepts_offers,
        photos_base64: photos,
        photo_urls: photoUrls,
        lat: locality.lat, lng: locality.lng,
        city: locality.city, state: locality.state, country: locality.country,
        radius_level: form.radius_level,
      });
      toast.success("Listing posted!");
      nav(`/marketplace/${data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't post listing.");
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-3xl mx-auto px-6 py-10" data-testid="marketplace-new-page">
      <div className="overline">MARKETPLACE</div>
      <h1 className="font-display text-4xl mt-2">Post a listing</h1>

      <form onSubmit={submit} className="mt-8 space-y-5">
        <div className="grid sm:grid-cols-2 gap-4">
          <label className="block sm:col-span-2">
            <span className="text-xs font-mono uppercase tracking-widest" style={{ color: "var(--wofy-text-soft)" }}>Title</span>
            <input
              data-testid="new-title"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="e.g. Vintage Trek 520 touring bike"
              maxLength={140}
              className="mt-1 w-full rounded-lg border px-3 py-2 text-sm"
              style={{ borderColor: "var(--wofy-border)" }}
              required
            />
          </label>

          <label className="block">
            <span className="text-xs font-mono uppercase tracking-widest" style={{ color: "var(--wofy-text-soft)" }}>Type</span>
            <select
              data-testid="new-type"
              value={form.listing_type}
              onChange={(e) => setForm({ ...form, listing_type: e.target.value })}
              className="mt-1 w-full rounded-lg border px-3 py-2 text-sm"
              style={{ borderColor: "var(--wofy-border)" }}
            >
              <option value="for_sale">For sale</option>
              <option value="looking_for">Looking for (buy request)</option>
            </select>
          </label>

          <label className="block">
            <span className="text-xs font-mono uppercase tracking-widest" style={{ color: "var(--wofy-text-soft)" }}>Category</span>
            <select
              data-testid="new-category"
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              className="mt-1 w-full rounded-lg border px-3 py-2 text-sm"
              style={{ borderColor: "var(--wofy-border)" }}
            >
              {(meta?.categories || []).map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
          </label>

          <label className="block">
            <span className="text-xs font-mono uppercase tracking-widest" style={{ color: "var(--wofy-text-soft)" }}>Condition</span>
            <select
              data-testid="new-condition"
              value={form.condition}
              onChange={(e) => setForm({ ...form, condition: e.target.value })}
              className="mt-1 w-full rounded-lg border px-3 py-2 text-sm"
              style={{ borderColor: "var(--wofy-border)" }}
            >
              <option value="new">New</option>
              <option value="like_new">Like new</option>
              <option value="good">Good</option>
              <option value="fair">Fair</option>
              <option value="for_parts">For parts</option>
              <option value="n_a">Not applicable</option>
            </select>
          </label>

          <label className="block">
            <span className="text-xs font-mono uppercase tracking-widest" style={{ color: "var(--wofy-text-soft)" }}>Radius</span>
            <select
              data-testid="new-radius"
              value={form.radius_level}
              onChange={(e) => setForm({ ...form, radius_level: e.target.value })}
              className="mt-1 w-full rounded-lg border px-3 py-2 text-sm"
              style={{ borderColor: "var(--wofy-border)" }}
            >
              <option value="neighborhood">Neighborhood (~5 km)</option>
              <option value="city">City (~25 km)</option>
              <option value="state">State (~200 km)</option>
              <option value="country">Country</option>
              <option value="global">Anywhere</option>
            </select>
          </label>
        </div>

        {/* Pricing block */}
        <div className="rounded-lg border p-4" style={{ borderColor: "var(--wofy-border)" }}>
          <div className="text-xs font-mono uppercase tracking-widest mb-3" style={{ color: "var(--wofy-text-soft)" }}>Pricing</div>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2 rounded-lg border px-3 py-2" style={{ borderColor: "var(--wofy-border)" }}>
              <DollarSign className="w-4 h-4 opacity-50" />
              <input
                data-testid="new-price"
                type="number" min="0" step="0.01"
                value={form.price}
                onChange={(e) => setForm({ ...form, price: e.target.value })}
                placeholder={form.category === "free_stuff" ? "Free" : "e.g. 350"}
                className="outline-none text-sm w-24"
                disabled={form.category === "free_stuff"}
              />
            </div>
            <label className="text-sm flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                data-testid="new-accepts-offers"
                checked={form.accepts_offers}
                onChange={(e) => setForm({ ...form, accepts_offers: e.target.checked })}
              />
              Accepts offers (OBO)
            </label>
          </div>
          <p className="text-xs mt-2" style={{ color: "var(--wofy-text-soft)" }}>
            Set a price, tick "Accepts offers", or pick <b>Free Stuff</b> as the category.
          </p>
        </div>

        <label className="block">
          <span className="text-xs font-mono uppercase tracking-widest" style={{ color: "var(--wofy-text-soft)" }}>Description</span>
          <textarea
            data-testid="new-description"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            rows={5}
            maxLength={4000}
            placeholder="What are you selling / looking for? Include size, brand, condition details, why you're selling, etc."
            className="mt-1 w-full rounded-lg border px-3 py-2 text-sm"
            style={{ borderColor: "var(--wofy-border)" }}
          />
        </label>

        {/* Photos */}
        <div className="rounded-lg border p-4" style={{ borderColor: "var(--wofy-border)" }}>
          <div className="text-xs font-mono uppercase tracking-widest mb-3" style={{ color: "var(--wofy-text-soft)" }}>Photos (up to 8)</div>
          <div className="flex flex-wrap gap-2 mb-3">
            <button
              type="button"
              data-testid="new-photo-upload-btn"
              onClick={() => fileRef.current?.click()}
              className="wofy-btn-ghost text-sm"
            >
              <Camera className="w-4 h-4" /> Upload / take photo
            </button>
            <input ref={fileRef} type="file" accept="image/*" multiple capture="environment" onChange={onFile} className="hidden" data-testid="new-photo-input" />
            <div className="flex items-center gap-2 rounded-lg border px-2 py-1" style={{ borderColor: "var(--wofy-border)" }}>
              <LinkIcon className="w-3.5 h-3.5 opacity-50" />
              <input
                data-testid="new-photo-url-input"
                value={photoUrl}
                onChange={(e) => setPhotoUrl(e.target.value)}
                placeholder="…or paste a photo URL"
                className="outline-none text-sm w-56"
              />
              <button type="button" onClick={addPhotoUrl} className="text-xs underline">Add</button>
            </div>
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
            {photos.map((p, i) => (
              <div key={`u-${i}`} className="relative aspect-square rounded overflow-hidden">
                <img src={p} alt="" className="w-full h-full object-cover" />
                <button type="button" onClick={() => setPhotos(photos.filter((_, j) => j !== i))} className="absolute top-0 right-0 bg-black/60 text-white text-[10px] px-1">✕</button>
              </div>
            ))}
            {photoUrls.map((p, i) => (
              <div key={`url-${i}`} className="relative aspect-square rounded overflow-hidden">
                <img src={p} alt="" className="w-full h-full object-cover" />
                <button type="button" onClick={() => setPhotoUrls(photoUrls.filter((_, j) => j !== i))} className="absolute top-0 right-0 bg-black/60 text-white text-[10px] px-1">✕</button>
              </div>
            ))}
          </div>
        </div>

        <button
          data-testid="new-submit-btn"
          type="submit"
          disabled={busy}
          className="w-full wofy-btn-primary justify-center text-sm disabled:opacity-60"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          {busy ? "Posting…" : "Post listing"}
        </button>
      </form>
    </div>
  );
}
